import { Edge, Node } from "@xyflow/react";
import { UpdateType, UpdatedNodeInfo } from "./types";
import { RFNodeData } from "./utils";
import { LogicalOperator } from "@unlikelyai-magic/shared/data-access/api";
import { v4 as uuid } from "uuid";


function addNode(node: Node, updateInfo: UpdatedNodeInfo): Node {
  return {
    ...node,
    type: "edited",
    data: {
      ...node.data,
      editType: "ADD",
      updateInfo: {
        original: updateInfo.original,
        explanation: updateInfo.explanation,
        note: updateInfo.note,
      },
    },
  };
}

function deleteNode(node: Node, updateInfo: UpdatedNodeInfo): Node {
  return {
    ...node,
    type: "edited",
    data: {
      ...node.data,
      editType: "DELETE",
      updateInfo: {
        original: updateInfo.original,
        explanation: updateInfo.explanation,
        note: updateInfo.note,
      },
    },
  };
}

function editNode(node: Node, updateInfo: UpdatedNodeInfo): Node {
    return {
      ...node,
      type: "edited",
      data: {
        ...node.data,
        editType: "EDIT",
        updateInfo: {
          original: updateInfo.original,
          rephrased: updateInfo.rephrased,
          explanation: updateInfo.explanation,
          note: updateInfo.note,
        },
      },
    };
}

function automaticEditNode(node: Node, updateInfo: UpdatedNodeInfo): Node {
  return {
    ...node,
    type: "automaticEditNode",
    data: {
      ...node.data,
      updateType: updateInfo.updateType,
      updateInfo: {
        original: updateInfo.original,
        explanation: updateInfo.explanation,
        note: updateInfo.note,
      },
    },
  };
}


function mergeNodes(oldNodes: Node[], updateInfo: UpdatedNodeInfo, edges: Edge[]): { nodes: Map<string,Node>; edges: Map<string,Edge> } {
  const parentNodeId = edges.find((e) => e.target === oldNodes[0].id)?.source;
  const productTreeId = oldNodes[0].data.productTreeId as string;
  if (!parentNodeId) {
    return { nodes: new Map<string,Node>(), edges: new Map<string,Edge>() };
  }

  const deleteResults = oldNodes.map((node) => deleteNode(node, updateInfo));
  const mergedHead = {
    id: uuid(),
    type: "editTrace",
    position: { x: 0, y: 0 },
    parentId: productTreeId,
    data: {
      productTreeId: productTreeId,
      operation: "MERGE",
      explanation: updateInfo.explanation,
    },
  }
  const mergedChild = {
    id: uuid(),
    type: "edited",
    position: { x: 0, y: 0 },
    parentId: productTreeId,
    data: {
      productTreeId: productTreeId,
      editType: "ADD",
      question: updateInfo.rephrased,
      updateInfo: {
        explanation: updateInfo.explanation,
        note: updateInfo.note,
      },
    },
  }
  const mergedHeadEdge = {
    id: `${parentNodeId}-${mergedHead.id}`,
    type: "custom-edge",
    source: parentNodeId,
    target: mergedHead.id,
    animated: true,
  }
  const mergedChildEdges = deleteResults.map((node) => {
    return {
      id: `${node.id}-${mergedChild.id}`,
      type: "custom-edge",
      source: node.id,
      target: mergedChild.id,
      animated: true,
    }
  });
  const mergedOriginalEdges = deleteResults.map((node) => {
    return {
      id: `${mergedHead.id}-${node.id}`,
      type: "custom-edge",
      source: mergedHead.id,
      target: node.id,
      animated: true,
    }
  });
  return {
    nodes: new Map<string, Node>([[mergedHead.id, mergedHead], [mergedChild.id, mergedChild],...deleteResults.map((n): [string, Node] => [n.id, n])]),
    edges: new Map<string, Edge>([
      [mergedHeadEdge.id, mergedHeadEdge],
      ...mergedChildEdges.map((e): [string, Edge] => [e.id, e]),
      ...mergedOriginalEdges.map((e): [string, Edge] => [e.id, e])
    ])
  };
}


function splitNodes(oldNode: Node, updateInfo: UpdatedNodeInfo, edges: Edge[]): { nodes: Map<string,Node>; edges: Map<string,Edge> } {
  const parentNodeId = edges.find((e) => e.target === oldNode.id)?.source;
  const productTreeId = oldNode.data.productTreeId as string;
  if (!parentNodeId) {
    return { nodes: new Map<string,Node>(), edges: new Map<string,Edge>() };
  }
  const deleteResult = deleteNode(oldNode, updateInfo);
  const splitHead = {
    id: uuid(),
    type: "editTrace",
    position: { x: 0, y: 0 },
    parentId: productTreeId,
    data: {
      productTreeId: productTreeId,
      operation: "SPLIT",
      explanation: updateInfo.explanation,
    },
  }
  const splitOR = {
    id: uuid(),
    type: "connective",
    position: { x: 0, y: 0 },
    parentId: productTreeId,
    data: {
      productTreeId: productTreeId,
      label: oldNode.data.label,
      logicalOperator: LogicalOperator.OR,
    },
  }
  const splitChildren = updateInfo.splitText?.map((text) => {
    return {
      id: uuid(),
      type: "edited",
      position: { x: 0, y: 0 },
      parentId: productTreeId,
      data: {
        productTreeId: productTreeId,
        editType: "ADD",
        question: text,
        updateInfo: {
          explanation: updateInfo.explanation,
          note: updateInfo.note,
        },
      },
    }
  }) ?? [];
  const splitHeadEdge = {
    id: `${parentNodeId}-${splitHead.id}`,
    type: "custom-edge",
    source: parentNodeId,
    target: splitHead.id,
    animated: true,
  }
  const splitOREdge = {
    id: `${splitHead.id}-${splitOR.id}`,
    type: "custom-edge",
    source: splitHead.id,
    target: splitOR.id,
    animated: true,
  }
  const splitChildEdges = splitChildren.map((child) => {
    return {
      id: `${splitOR.id}-${child.id}`,
      type: "custom-edge",
      source: splitOR.id,
      target: child.id,
      animated: true,
    }
  });
  const splitOriginalEdge = {
      id: `${splitHead.id}-${deleteResult.id}`,
      type: "custom-edge",
      source: splitHead.id,
      target: deleteResult.id,
      animated: true,
  }
  return { nodes: new Map<string,Node>([[deleteResult.id,deleteResult], [splitHead.id,splitHead], [splitOR.id,splitOR], ...splitChildren.map((n): [string, Node] => [n.id, n])]),
    edges: new Map<string,Edge>([[splitHeadEdge.id,splitHeadEdge], [splitOREdge.id,splitOREdge], ...splitChildEdges.map((e): [string, Edge] => [e.id, e]), [splitOriginalEdge.id,splitOriginalEdge]]) };
}


export function applyNodeUpdates(
  nodes: Node[],
  edges: Edge[],
  updatedNodes: UpdatedNodeInfo[]
): { nodes: Node[]; edges: Edge[] } {
  if (updatedNodes.length === 0) {
    return { nodes, edges };
  }

  // Create lookup map for updated nodes by node name
  const nodesMap = new Map<string, Node>();
  for (const node of nodes) {
    nodesMap.set((node.data as RFNodeData).label, node);
  }

  const finalNodes = new Map<string, Node>(nodes.map((n) => [n.id, n]));
  const finalEdges = new Map<string, Edge>(edges.map((e) => [e.id, e]));

  updatedNodes.forEach((updateInfo) => {
    const node = nodesMap.get(updateInfo.nodeName);


    switch (updateInfo.updateType) {
      case UpdateType.LOGICALLY_ELIMINATED:
      case UpdateType.PRE_EVALUATED:{
        if (node) {
          const addResult = automaticEditNode(node, updateInfo);
          finalNodes.set(addResult.id, addResult);
        }
        break;
      }
      case UpdateType.AUTOMATIC_INSERTION:
      case UpdateType.INLINED:
      case UpdateType.MANUAL_EDIT: {
        if (node) {
          const addResult = addNode(node,updateInfo);
          finalNodes.set(addResult.id, addResult);
        }
        break;
      }
      case UpdateType.REPHRASING:
      case UpdateType.HIL_CONTEXT: {
        if (node) {
        const editedNode = editNode(node, updateInfo);
        finalNodes.set(editedNode.id, editedNode);
        }
        break;
      }
      case UpdateType.MERGE: {
        if (node) {
          const otherNodes = (updateInfo.mergedWith ?? [])
            .map((nodeName) => nodes.find((n) => (n.data as RFNodeData).label === nodeName))
            .filter((n): n is Node => n !== undefined);
          const oldNodes = [node, ...otherNodes];
          oldNodes.forEach((n) => {
            const edgeToDelete = edges.find((e) => e.target === n.id);
            if (edgeToDelete) {
              finalEdges.delete(edgeToDelete.id);
            }
          });
          const mergeResult = mergeNodes(oldNodes, updateInfo, edges);
          mergeResult.nodes.forEach((n, id) => finalNodes.set(id, n));
          mergeResult.edges.forEach((e, id) => finalEdges.set(id, e));
        }
        break;
      }
      case UpdateType.SPLIT_OUT: {
        if (node) {
          const edgeToDelete = edges.find((e) => e.target === node.id);
          if (edgeToDelete) {
            finalEdges.delete(edgeToDelete.id);
          }
          const splitResult = splitNodes(node, updateInfo, edges);
          splitResult.nodes.forEach((n, id) => finalNodes.set(id, n));
          splitResult.edges.forEach((e, id) => finalEdges.set(id, e));
        }
        break;
      }
    }
  });

  return { nodes: Array.from(finalNodes.values()), edges: Array.from(finalEdges.values()) };
}
